import {
  getMatterTemplateDictionaryCardStateKey,
  MatterTemplateDictionaryCard,
  type MatterTemplateDictionaryCardState,
} from "@/components/features/settings/defaults/matter-template-dictionary/MatterTemplateDictionaryCard";
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Typography,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  formatTemplateDictionaryTitle,
  templateDictionaryItems,
} from "@/lib/data/static/templateDictionaryItems";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { type ColumnFiltersState } from "@tanstack/react-table";
import { sortBy, startCase } from "lodash";
import { Check, PlusCircle } from "lucide-react";
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export type TemplateDictionaryItem = (typeof templateDictionaryItems)[number];

function areStringArraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((value, index) => value === b[index]);
}

function normalizeTemplateDictionaryItems(items: TemplateDictionaryItem[]) {
  return items.map((item) => ({
    item,
    parentTitle: formatTemplateDictionaryTitle(startCase(item.parent)),
    searchText: [
      item.title,
      item.variable,
      startCase(item.parent),
      item.exampleValue,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  }));
}

function getTypeFilterOptions(items: TemplateDictionaryItem[]) {
  return Array.from(
    new Map(
      normalizeTemplateDictionaryItems(items).map(({ item, parentTitle }) => [
        item.parent,
        { label: parentTitle, value: item.parent },
      ]),
    ).values(),
  ).sort((a, b) => a.label.localeCompare(b.label));
}

const parentFilterId = "Parent";

interface TemplateDictionaryCardItemProps {
  item: TemplateDictionaryItem;
  selectLabel?: string;
  onCopy: (text: string) => void;
  onVariableSelect?: (variable: string) => void;
  cardState?: MatterTemplateDictionaryCardState;
  onCardStateChange?: (
    key: string,
    state: MatterTemplateDictionaryCardState,
  ) => void;
  userRoleLabels?: string[];
}

const TemplateDictionaryCardItem = memo(
  ({
    item,
    selectLabel,
    onCopy,
    onVariableSelect,
    cardState,
    onCardStateChange,
    userRoleLabels,
  }: TemplateDictionaryCardItemProps) => {
    const stateKey = useMemo(
      () => getMatterTemplateDictionaryCardStateKey(item),
      [item],
    );
    const handleStateChange = useCallback(
      (state: MatterTemplateDictionaryCardState) => {
        onCardStateChange?.(stateKey, state);
      },
      [onCardStateChange, stateKey],
    );

    return (
      <MatterTemplateDictionaryCard
        item={item}
        onCopy={onCopy}
        onSelect={onVariableSelect}
        selectLabel={selectLabel}
        state={cardState}
        onStateChange={handleStateChange}
        userRoleLabels={userRoleLabels}
      />
    );
  },
);
TemplateDictionaryCardItem.displayName = "TemplateDictionaryCardItem";

interface MatterTemplateDictionaryProps {
  onVariableSelect?: (variable: string) => void;
  selectLabel?: string;
  cardGridClassName?: string;
  allowedParents?: readonly TemplateDictionaryItem["parent"][];
  toolbarClassName?: string;
  searchInputClassName?: string;
  controlsClassName?: string;
  collapseButtonClassName?: string;
  cardStates?: Record<string, MatterTemplateDictionaryCardState>;
  onCardStateChange?: (
    key: string,
    state: MatterTemplateDictionaryCardState,
  ) => void;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  openSections?: string[];
  onOpenSectionsChange?: (sections: string[]) => void;
}

export const MatterTemplateDictionary = ({
  onVariableSelect,
  selectLabel,
  cardGridClassName,
  allowedParents,
  toolbarClassName,
  searchInputClassName,
  controlsClassName,
  collapseButtonClassName,
  cardStates,
  onCardStateChange,
  globalFilter,
  onGlobalFilterChange,
  columnFilters,
  onColumnFiltersChange,
  openSections,
  onOpenSectionsChange,
}: MatterTemplateDictionaryProps) => {
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const [internalGlobalFilter, setInternalGlobalFilter] = useState("");
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([]);
  const [internalOpenSections, setInternalOpenSections] = useState<string[]>();
  const allowedParentSet = useMemo(
    () => new Set(allowedParents ?? []),
    [allowedParents],
  );
  const hasAllowedParentRestriction = allowedParentSet.size > 0;
  const filteredTemplateDictionaryItems = useMemo(
    () =>
      hasAllowedParentRestriction
        ? templateDictionaryItems.filter((item) =>
            allowedParentSet.has(item.parent),
          )
        : templateDictionaryItems,
    [allowedParentSet, hasAllowedParentRestriction],
  );
  const normalizedItems = useMemo(
    () => normalizeTemplateDictionaryItems(filteredTemplateDictionaryItems),
    [filteredTemplateDictionaryItems],
  );
  const typeFilterOptions = useMemo(
    () => getTypeFilterOptions(filteredTemplateDictionaryItems),
    [filteredTemplateDictionaryItems],
  );
  const userRoleLabels = useMemo(
    () =>
      sortBy(
        (matterSettingsData?.data.roles ?? [])
          .map((role) => role.label.trim())
          .filter((role): role is string => Boolean(role)),
      ),
    [matterSettingsData?.data.roles],
  );

  const resolvedGlobalFilter = globalFilter ?? internalGlobalFilter;
  const resolvedColumnFilters = columnFilters ?? internalColumnFilters;
  const resolvedOpenSectionsState = openSections ?? internalOpenSections;
  const handleCopy = useCallback((text: string) => {
    void navigator.clipboard.writeText(text);
  }, []);
  const deferredGlobalFilter = useDeferredValue(resolvedGlobalFilter);
  const normalizedSearchTerm = deferredGlobalFilter.trim().toLowerCase();
  const hasActiveSearch = normalizedSearchTerm.length > 0;
  const selectedParents = useMemo(() => {
    const parentFilter = resolvedColumnFilters.find(
      (filter) => filter.id === parentFilterId,
    );
    if (!Array.isArray(parentFilter?.value)) {
      return [];
    }
    return parentFilter.value.filter(
      (value): value is string => typeof value === "string",
    );
  }, [resolvedColumnFilters]);
  const selectedParentSet = useMemo(
    () => new Set(selectedParents),
    [selectedParents],
  );
  const filteredItems = useMemo(
    () =>
      normalizedItems
        .filter(({ item, searchText }) => {
          if (
            selectedParentSet.size > 0 &&
            !selectedParentSet.has(item.parent)
          ) {
            return false;
          }

          if (!normalizedSearchTerm) {
            return true;
          }

          return searchText.includes(normalizedSearchTerm);
        })
        .map(({ item }) => item),
    [normalizedItems, normalizedSearchTerm, selectedParentSet],
  );
  const sections = useMemo(() => {
    const groupedItems = filteredItems.reduce((acc, item) => {
      const parentItems = acc.get(item.parent) ?? [];
      parentItems.push(item);
      acc.set(item.parent, parentItems);
      return acc;
    }, new Map<TemplateDictionaryItem["parent"], TemplateDictionaryItem[]>());

    return Array.from(groupedItems.entries())
      .map(([parent, items]) => ({
        key: parent,
        title: startCase(parent),
        items: items.sort((a, b) => a.title.localeCompare(b.title)),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [filteredItems]);

  const sectionKeys = useMemo(
    () => sections.map((section) => section.key),
    [sections],
  );
  const sectionKeySet = useMemo(
    () => new Set<string>(sectionKeys),
    [sectionKeys],
  );
  const previousSearchExpansionKeyRef = useRef<string>();

  const resolvedOpenSections = resolvedOpenSectionsState ?? sectionKeys;

  useEffect(() => {
    if (hasActiveSearch) {
      const searchExpansionKey = `${normalizedSearchTerm}::${sectionKeys.join("|")}`;
      if (previousSearchExpansionKeyRef.current === searchExpansionKey) {
        return;
      }

      previousSearchExpansionKeyRef.current = searchExpansionKey;

      if (!areStringArraysEqual(resolvedOpenSectionsState ?? [], sectionKeys)) {
        if (onOpenSectionsChange) {
          onOpenSectionsChange(sectionKeys);
          return;
        }
        setInternalOpenSections(sectionKeys);
      }
      return;
    }

    previousSearchExpansionKeyRef.current = undefined;

    if (resolvedOpenSectionsState !== undefined) {
      const filtered = resolvedOpenSectionsState.filter((key) =>
        sectionKeySet.has(key),
      );
      if (!areStringArraysEqual(filtered, resolvedOpenSectionsState)) {
        if (onOpenSectionsChange) {
          onOpenSectionsChange(filtered);
          return;
        }
        setInternalOpenSections(filtered);
      }
      return;
    }

    if (sectionKeys.length > 0) {
      setInternalOpenSections(sectionKeys);
    }
  }, [
    hasActiveSearch,
    normalizedSearchTerm,
    onOpenSectionsChange,
    resolvedOpenSectionsState,
    sectionKeys,
    sectionKeySet,
  ]);

  const areAllClosed = resolvedOpenSections.length === 0;

  const handleToggleAll = () => {
    if (areAllClosed) {
      const nextSections = sections.map((section) => section.key);
      if (onOpenSectionsChange) {
        onOpenSectionsChange(nextSections);
        return;
      }
      setInternalOpenSections(nextSections);
      return;
    }

    if (onOpenSectionsChange) {
      onOpenSectionsChange([]);
      return;
    }
    setInternalOpenSections([]);
  };

  const setResolvedGlobalFilter = (value: string) => {
    if (onGlobalFilterChange) {
      onGlobalFilterChange(value);
      return;
    }
    setInternalGlobalFilter(value);
  };

  const setResolvedColumnFilters = (nextFilters: ColumnFiltersState) => {
    if (onColumnFiltersChange) {
      onColumnFiltersChange(nextFilters);
      return;
    }
    setInternalColumnFilters(nextFilters);
  };

  const handleParentFilterToggle = (value: string) => {
    const nextSelectedParents = new Set(selectedParents);
    if (nextSelectedParents.has(value)) {
      nextSelectedParents.delete(value);
    } else {
      nextSelectedParents.add(value);
    }

    const nextParentValues = Array.from(nextSelectedParents);
    const nextFilters = resolvedColumnFilters.filter(
      (filter) => filter.id !== parentFilterId,
    );

    if (nextParentValues.length > 0) {
      nextFilters.push({
        id: parentFilterId,
        value: nextParentValues,
      });
    }

    setResolvedColumnFilters(nextFilters);
  };

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between",
          controlsClassName,
        )}
      >
        <div className="flex-1">
          <div className={cn("flex flex-wrap gap-2", toolbarClassName)}>
            <Input
              placeholder="Search..."
              value={resolvedGlobalFilter}
              onChange={(event) => {
                setResolvedGlobalFilter(event.target.value);
              }}
              data-testid="data-table-global-search"
              className={cn("h-9 w-[150px] lg:w-[250px]", searchInputClassName)}
            />
            {!hasAllowedParentRestriction && (
              <Popover modal={false}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="border-dashed">
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Type
                    {selectedParents.length > 0 && (
                      <>
                        <Separator
                          orientation="vertical"
                          className="h-4 w-px"
                        />
                        <Badge variant="secondary" className="ml-2">
                          {selectedParents.length}
                        </Badge>
                      </>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  portalled={false}
                  className="max-h-[60vh] overflow-hidden p-0"
                  onWheel={(event) => {
                    event.stopPropagation();
                  }}
                  onTouchMove={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <Command className="max-h-[60vh]">
                    <CommandList
                      className="max-h-[50vh] overscroll-contain"
                      onWheel={(event) => {
                        event.stopPropagation();
                      }}
                      onTouchMove={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <CommandEmpty>No results found.</CommandEmpty>
                      <CommandGroup>
                        {typeFilterOptions.map((option) => {
                          const isSelected = selectedParentSet.has(
                            option.value,
                          );
                          return (
                            <CommandItem
                              key={option.value}
                              onSelect={() => {
                                handleParentFilterToggle(option.value);
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className={cn(
                                    "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                                    isSelected
                                      ? "bg-primary text-primary-foreground"
                                      : "opacity-50 [&_svg]:invisible",
                                  )}
                                >
                                  <Check className="h-4 w-4" />
                                </div>
                                {option.label}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                      {selectedParents.length > 0 && (
                        <>
                          <Separator className="my-1" />
                          <CommandGroup>
                            <CommandItem
                              onSelect={() => {
                                setResolvedColumnFilters(
                                  resolvedColumnFilters.filter(
                                    (filter) => filter.id !== parentFilterId,
                                  ),
                                );
                              }}
                              className="justify-center text-center"
                            >
                              Clear
                            </CommandItem>
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className={collapseButtonClassName}
          onClick={handleToggleAll}
        >
          {areAllClosed ? "Expand all" : "Collapse all"}
        </Button>
      </div>
      <div className="py-2">
        {filteredItems.length > 0 && sections.length > 0 ? (
          <Accordion
            type="multiple"
            value={resolvedOpenSections}
            onValueChange={(value) => {
              if (onOpenSectionsChange) {
                onOpenSectionsChange(value);
                return;
              }
              setInternalOpenSections(value);
            }}
            className="space-y-4"
          >
            {sections.map((section) => (
              <AccordionItem
                key={section.key}
                value={section.key}
                className="bg-card px-4"
              >
                <AccordionTrigger className="text-left text-base font-semibold">
                  <span className="text-lg">{section.title}</span>
                </AccordionTrigger>
                <AccordionContent className="pt-4">
                  <div
                    className={cn(
                      "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3",
                      cardGridClassName,
                    )}
                  >
                    {section.items.map((item) => (
                      <TemplateDictionaryCardItem
                        key={item.variable}
                        item={item}
                        onCopy={handleCopy}
                        onVariableSelect={onVariableSelect}
                        selectLabel={selectLabel}
                        cardState={
                          cardStates?.[
                            getMatterTemplateDictionaryCardStateKey(item)
                          ]
                        }
                        onCardStateChange={onCardStateChange}
                        userRoleLabels={userRoleLabels}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div className="col-span-full">
            <Typography variant="subHeading">
              No template dictionary variables selected. Please select a type to
              view the variables.
            </Typography>
          </div>
        )}
      </div>
    </>
  );
};
